import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Flame, Loader2 } from "lucide-react";
import { UserRow } from "@/components/app/UserRow";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const TABS = [
  { id: "discover", label: "Discover" },
  { id: "people", label: "People" },
  { id: "all", label: "All Users" },
];
const CATS = ["Trending", "Creator", "Musician", "Artist", "Athlete", "Entrepreneur", "Developer", "Trader", "Gamer", "Model"];

export default function Explore() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState("discover");
  const [q, setQ] = useState(params.get("q") || "");
  const [cat, setCat] = useState("Trending");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const reqId = useRef(0);

  useEffect(() => { if (params.get("q")) setTab("people"); }, [params]);

  const run = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true); setSkip(0); setHasMore(false);
    try {
      let data = [];
      if (tab === "discover") {
        data = q.trim() ? await api.searchUsers(q.trim()) : await api.discover(cat === "Trending" ? "" : cat);
      } else if (tab === "people") {
        data = q.trim() ? await api.searchUsers(q.trim()) : await api.discover("");
      } else {
        const r = await api.allUsers(q.trim(), 0, 24);
        data = r.users; setHasMore(r.has_more); setSkip(24);
      }
      if (id === reqId.current) setList(data);
    } catch { if (id === reqId.current) setList([]); }
    finally { if (id === reqId.current) setLoading(false); }
    // stable api/setters/reqId ref; tab/q/cat are the real inputs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, q, cat]);

  useEffect(() => { const t = setTimeout(run, 300); return () => clearTimeout(t); }, [run]);

  const loadMore = async () => {
    setLoadingMore(true);
    try { const r = await api.allUsers(q.trim(), skip, 24); setList((l) => [...l, ...r.users]); setHasMore(r.has_more); setSkip((s) => s + 24); }
    catch {} finally { setLoadingMore(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-5 md:px-6">
      <div className="sticky top-0 z-10 -mx-4 mb-4 bg-am-bg/90 px-4 pb-3 pt-1 backdrop-blur md:mx-0 md:px-0">
        <div className="flex items-center gap-2 rounded-full border border-am-border bg-am-surface2 px-4">
          <Search className="h-4 w-4 text-am-text3" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="explore-search" placeholder="Search by name, @username or profession…" className="w-full bg-transparent py-3 text-sm text-white placeholder:text-am-text3 outline-none" />
        </div>
        <div className="mt-3 flex gap-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`explore-tab-${t.id}`}
              className={cn("relative flex-1 rounded-full py-1.5 text-sm font-semibold transition-colors", tab === t.id ? "text-white" : "text-am-text2 hover:text-white")}>
              {t.label}
              {tab === t.id && <span className="absolute -bottom-1 left-1/2 h-[3px] w-8 -translate-x-1/2 rounded-full aura-gradient" />}
            </button>
          ))}
        </div>
        {tab === "discover" && !q && (
          <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`explore-cat-${c.toLowerCase()}`} className={cn("shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold transition-all", cat === c ? "aura-gradient text-black" : "border border-am-border bg-am-surface2 text-am-text2 hover:text-white")}>{c}</button>
            ))}
          </div>
        )}
      </div>

      {tab === "discover" && !q && (
        <button onClick={() => navigate("/leaderboard")} className="mb-4 flex w-full items-center gap-3 rounded-2xl border border-aura-magenta/20 bg-gradient-to-r from-am-surface to-am-bg p-3.5 text-left">
          <span className="grid h-10 w-10 place-items-center rounded-xl aura-gradient"><Flame className="h-5 w-5 text-black" /></span>
          <div className="flex-1"><div className="text-sm font-bold text-white">Fresh Aura leaderboard</div><div className="text-xs text-am-text2">See who's gaining Aura momentum</div></div>
        </button>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-am-text3" /></div>
      ) : list.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-sm font-semibold text-white">{q ? `No results for "${q}"` : "Nothing here yet"}</p>
          <p className="mt-1 text-xs text-am-text2">Try another search or tab.</p>
        </div>
      ) : (
        <>
          {tab === "all" && !q && <p className="mb-2 text-xs font-semibold text-am-text2">Everyone on AuraMax</p>}
          <div className="space-y-1">{list.map((u) => <UserRow key={u.user_id} u={u} />)}</div>
          {tab === "all" && hasMore && (
            <button onClick={loadMore} disabled={loadingMore} data-testid="explore-load-more" className="mx-auto mt-4 flex items-center gap-2 rounded-full border border-am-border-strong px-6 py-2 text-sm font-semibold text-white hover:bg-white/5">
              {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Load more
            </button>
          )}
        </>
      )}
    </div>
  );
}
